import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Heart, ShoppingBag, ArrowLeft, Info, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { ProductImageGallery } from '../components/product/ProductImageGallery';
import { ProductPricingBlock } from '../components/product/ProductPricingBlock';
import { ProductSizeSelector } from '../components/product/ProductSizeSelector';
import { ProductRecommendations } from '../components/product/ProductRecommendations';
import { CompleteTheLook } from '../components/product/CompleteTheLook';
import { ColorVariants } from '../components/product/ColorVariants';
import { ShareModal } from '../components/product/ShareModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};
const DISCLAIMER = "Prices shown do not include shipping costs. Items are sourced from reputable boutiques globally. All products are shipped to Dubai and quality checked. Shipping fees are calculated after sourcing.";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [isFav, setIsFav] = useState(false);
  const [inBag, setInBag] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [bagLoading, setBagLoading] = useState(false);
  const [bagMsg, setBagMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [imgTransition, setImgTransition] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [requestedSize, setRequestedSize] = useState('');
  const [sizeRequestSent, setSizeRequestSent] = useState(false);
  const [sizeRequestLoading, setSizeRequestLoading] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products/${id}`)
      .then(r => { setProduct(r.data); setActiveImg(0); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/shop'); });
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    const sessionId = sessionStorage.getItem('hl_session') || (() => {
      const s = `sess_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('hl_session', s);
      return s;
    })();
    axios.post(`${API}/products/${id}/view`, {}, {
      headers: { 'X-Session-Id': sessionId },
      withCredentials: true,
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (id) {
      axios.get(`${API}/products/${id}/recommendations`, { withCredentials: true }).then(r => setRecommendations(r.data)).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (user && product) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true })
        .then(r => setIsFav(r.data.includes(product.product_id))).catch(() => {});
      axios.get(`${API}/bag`, { withCredentials: true })
        .then(r => setInBag(r.data.some(i => i.product_id === product.product_id))).catch(() => {});
    }
  }, [user, product]);

  const switchImage = (idx) => {
    if (idx === activeImg) return;
    setImgTransition(true);
    setTimeout(() => { setActiveImg(idx); setImgTransition(false); }, 180);
  };
  const prevImage = (e) => { e?.stopPropagation(); switchImage((activeImg - 1 + (product?.images?.length || 1)) % (product?.images?.length || 1)); };
  const nextImage = (e) => { e?.stopPropagation(); switchImage((activeImg + 1) % (product?.images?.length || 1)); };

  const toggleFav = async () => {
    if (!user) { navigate('/auth'); return; }
    if (isFav) {
      await axios.delete(`${API}/favorites/${product.product_id}`, { withCredentials: true });
      setIsFav(false);
    } else {
      await axios.post(`${API}/favorites`, { product_id: product.product_id }, { withCredentials: true });
      setIsFav(true);
    }
  };

  const addToBag = async () => {
    if (!user) { navigate('/auth'); return; }
    if (inBag) { navigate('/bag'); return; }
    setBagLoading(true);
    try {
      await axios.post(`${API}/bag`, { product_id: product.product_id, size: selectedSize || null }, { withCredentials: true });
      setInBag(true);
      setBagMsg('Added to your bag!');
      setTimeout(() => setBagMsg(''), 3000);
    } catch (e) {
      setBagMsg(e.response?.data?.detail || 'Failed to add to bag');
    } finally {
      setBagLoading(false);
    }
  };

  const handleSoldOutSizeClick = (size) => {
    if (!user) { navigate('/auth'); return; }
    setRequestedSize(size === requestedSize ? '' : size);
    setSizeRequestSent(false);
  };

  const submitSizeRequest = async () => {
    if (!user) { navigate('/auth'); return; }
    setSizeRequestLoading(true);
    try {
      await axios.post(`${API}/size-requests`, { product_id: product.product_id, size: requestedSize }, { withCredentials: true });
      setSizeRequestSent(true);
    } catch {
      devErr('[ProductDetail] submitSizeRequest failed');
    } finally {
      setSizeRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!product) return null;

  const images = product.images?.filter(Boolean).length ? product.images.filter(Boolean) : [product.images?.[0]].filter(Boolean);
  const savingsPct = Math.round(product.discount_percentage || 0);
  const origin = window.location.origin;
  const ogTitle = `${product.designer} — ${product.name} | HEIR LUXX`;
  const ogDesc = `Discover ${product.designer} ${product.name} for R${Number(product.final_price).toLocaleString('en-ZA', { maximumFractionDigits: 0 })} on HEIR LUXX — your luxury personal shopper.`;
  const ogImage = images[0] ? (images[0].startsWith('/') ? `${origin}${images[0]}` : images[0]) : '';
  const ogUrl = `${origin}/product/${product.product_id}`;

  return (
    <div className="bg-white min-h-screen pt-16 lg:pt-20 pb-24 lg:pb-0" data-testid="product-detail">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDesc} />
        <link rel="canonical" href={ogUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="HEIR LUXX" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:url" content={ogUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="product:price:amount" content={Number(product.final_price).toFixed(2)} />
        <meta property="product:price:currency" content="ZAR" />
        <meta property="product:brand" content={product.designer} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#999999] hover:text-[#0A0A0A] transition-colors font-work">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">

          <ProductImageGallery
            images={images}
            activeImg={activeImg}
            imgTransition={imgTransition}
            switchImage={switchImage}
            prevImage={prevImage}
            nextImage={nextImage}
            productName={product.name}
          />

          {/* Info Panel */}
          <div className="flex flex-col gap-6 lg:pt-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#AAAAAA] font-work">
              <Link to="/shop" className="hover:text-[#D4AF37]">Shop</Link>
              <span>/</span>
              <Link to={`/shop?category=${product.category}`} className="hover:text-[#D4AF37]">{product.category}</Link>
              <span>/</span>
              <span className="text-[#D4AF37]">{product.designer}</span>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2 font-work" data-testid="product-designer">
                {product.designer}
              </p>
              <h1 className="font-playfair text-3xl sm:text-4xl text-[#0A0A0A] leading-tight tracking-tight" data-testid="product-name">
                {product.name}
              </h1>
            </div>

            <ProductPricingBlock
              original_price={product.original_price}
              final_price={product.final_price}
              savings={product.savings}
              savingsPct={savingsPct}
            />

            <p className="text-sm text-[#555555] leading-relaxed font-work" data-testid="product-description">
              {product.description}
            </p>

            <ColorVariants productId={product.product_id} color={product.color} gender={product.gender} />

            <ProductSizeSelector
              sizes={product.sizes}
              available_sizes={product.available_sizes}
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              requestedSize={requestedSize}
              sizeRequestSent={sizeRequestSent}
              sizeRequestLoading={sizeRequestLoading}
              submitSizeRequest={submitSizeRequest}
              handleSoldOutSizeClick={handleSoldOutSizeClick}
              setRequestedSize={setRequestedSize}
            />

            {product.sizes?.length > 0 && (
              <div className="bg-[#F9F9F9] border-l-2 border-[#D4AF37] px-4 py-3" data-testid="custom-size-note">
                <p className="text-[11px] text-[#555555] font-work leading-relaxed">
                  <span className="font-semibold text-[#0A0A0A]">Need a different size?</span> Request a custom sourced size. Please note that special requested sizes may be subject to higher pricing depending on sourcing availability.
                </p>
              </div>
            )}

            {bagMsg && (
              <div className={`px-4 py-3 text-sm font-work ${bagMsg.includes('Added') ? 'bg-[#2E7D32]/10 text-[#2E7D32] border border-[#2E7D32]/20' : 'bg-[#D32F2F]/10 text-[#D32F2F] border border-[#D32F2F]/20'}`}>
                {bagMsg}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={addToBag}
                disabled={bagLoading}
                className="flex-1 flex items-center justify-center gap-2 bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-60"
                data-testid="add-to-bag-btn"
              >
                <ShoppingBag size={16} />
                {inBag ? 'View Bag' : (bagLoading ? 'Adding...' : 'Add to Bag')}
              </button>
              <button
                onClick={toggleFav}
                className={`w-14 h-14 border flex items-center justify-center transition-colors ${isFav ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]' : 'border-[#E5E5E5] text-[#0A0A0A] hover:border-[#D4AF37]'}`}
                data-testid="fav-product-btn"
              >
                <Heart size={18} className={isFav ? 'fill-current' : ''} />
              </button>
              <button
                onClick={() => setShowShare(true)}
                className="w-14 h-14 border border-[#E5E5E5] flex items-center justify-center text-[#0A0A0A] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="share-product-btn"
                aria-label="Share on Instagram"
              >
                <Share2 size={18} />
              </button>
            </div>

            {showShare && <ShareModal product={product} onClose={() => setShowShare(false)} />}

            <div className="border border-[#E5E5E5] p-4 mt-2">
              <div className="flex gap-2 items-start">
                <Info size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#999999] leading-relaxed font-work">{DISCLAIMER}</p>
              </div>
            </div>

            <CompleteTheLook productId={product.product_id} />
          </div>
        </div>
      </div>

      <ProductRecommendations recommendations={recommendations} />

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] px-4 py-3 flex gap-3 shadow-lg">
        <button
          onClick={addToBag}
          disabled={bagLoading}
          className="flex-1 flex items-center justify-center gap-2 bg-[#0A0A0A] text-white active:bg-[#D4AF37] active:text-[#0A0A0A] transition-colors py-3.5 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-60"
          data-testid="mobile-add-to-bag-btn"
        >
          <ShoppingBag size={15} />
          {inBag ? 'View Bag' : (bagLoading ? 'Adding...' : 'Add to Bag')}
        </button>
        <button
          onClick={toggleFav}
          className={`w-12 h-12 border flex items-center justify-center transition-colors flex-shrink-0 ${isFav ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]' : 'border-[#E5E5E5] text-[#0A0A0A]'}`}
          data-testid="mobile-fav-btn"
        >
          <Heart size={17} className={isFav ? 'fill-current' : ''} />
        </button>
      </div>
    </div>
  );
}
